import type { AlterlyEffectInstance } from "../effects/effectRegistry";

export type EffectStackSnapshot = {
  effects: AlterlyEffectInstance[];
  selectedId: string | null;
};

export type EffectStackHistoryState = {
  past: EffectStackSnapshot[];
  present: EffectStackSnapshot;
  future: EffectStackSnapshot[];
};

export type EffectStackHistoryAction =
  | { type: "commit"; snapshot: EffectStackSnapshot }
  | { type: "select"; selectedId: string | null }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "reset"; snapshot: EffectStackSnapshot };

const HISTORY_LIMIT = 100;

export function createEffectStackHistoryState(snapshot: EffectStackSnapshot): EffectStackHistoryState {
  return {
    past: [],
    present: snapshot,
    future: [],
  };
}

function sameSnapshot(a: EffectStackSnapshot, b: EffectStackSnapshot) {
  return a.effects === b.effects && a.selectedId === b.selectedId;
}

export function effectStackHistoryReducer(
  state: EffectStackHistoryState,
  action: EffectStackHistoryAction,
): EffectStackHistoryState {
  switch (action.type) {
    case "commit": {
      if (sameSnapshot(state.present, action.snapshot)) return state;
      const past = [...state.past, state.present];
      return {
        past: past.slice(Math.max(0, past.length - HISTORY_LIMIT)),
        present: action.snapshot,
        future: [],
      };
    }
    case "select": {
      if (state.present.selectedId === action.selectedId) return state;
      return {
        ...state,
        present: { ...state.present, selectedId: action.selectedId },
      };
    }
    case "undo": {
      const previous = state.past.at(-1);
      if (!previous) return state;
      return {
        past: state.past.slice(0, -1),
        present: previous,
        future: [state.present, ...state.future],
      };
    }
    case "redo": {
      const next = state.future[0];
      if (!next) return state;
      return {
        past: [...state.past, state.present].slice(-HISTORY_LIMIT),
        present: next,
        future: state.future.slice(1),
      };
    }
    case "reset":
      return createEffectStackHistoryState(action.snapshot);
  }
}
